/**
 * Estado de selecao do modal SeletorUsinasEolicas (WINDX-021).
 *
 * Por que `Set<number>` em `useRef` ao inves de `useState([...])`?
 *
 *   Toggle de checkbox numa lista de 1500 itens com `useState(Set)` ou
 *   `useState(Array)` causa re-render da arvore inteira do modal — cada
 *   linha precisa reavaliar `selecionadas.has(id)`. Com `useRef`, o Set
 *   muta em O(1) sem disparar reconciliacao; o re-render so acontece
 *   quando explicitamente chamamos `forceUpdate`.
 *
 *   Resultado pratico: o toggle responde em <16ms mesmo em CPU lenta
 *   (Lighthouse mobile slow 4G).
 *
 * API estavel (referencias preservadas entre renders):
 *   - `isSelecionada(id)`: O(1), nao reage
 *   - `toggle(id)`: dispara forceUpdate
 *   - `selecionarTodas(ids)`: batch + forceUpdate
 *   - `limpar()`: forceUpdate
 *   - `count`: numero atual de selecionadas (deriva)
 *   - `totalPotencia(usinasPorId)`: soma em MW (recebe lookup map)
 *   - `ids`: snapshot ordenado em number[] (recalculado a cada uso)
 */
import {
  useCallback, useMemo, useReducer, useRef,
} from 'react';
import { UsinaEolicaDenormalizada } from 'helper/windx';

interface UseSelecaoUsinasParams {
  inicial?: number[];
}

export interface UseSelecaoUsinasReturn {
  isSelecionada: (id: number) => boolean;
  toggle: (id: number) => void;
  selecionarTodas: (ids: number[]) => void;
  desselecionarTodas: (ids: number[]) => void;
  limpar: () => void;
  resetar: (ids: number[]) => void;
  count: number;
  totalPotencia: (usinasPorId: Map<number, UsinaEolicaDenormalizada>) => number;
  ids: number[];
}

export default function useSelecaoUsinas(
  { inicial = [] }: UseSelecaoUsinasParams = {},
): UseSelecaoUsinasReturn {
  const setRef = useRef<Set<number>>(new Set(inicial));
  // Contador-nonce: incrementa a cada mutacao para invalidar memos que
  // dependem do conteudo do Set (size sozinho nao basta — `resetar` pode
  // substituir um set de N itens por outro de N itens distintos).
  const [nonce, forceUpdate] = useReducer((x: number) => x + 1, 0);

  const isSelecionada = useCallback((id: number) => setRef.current.has(id), []);

  const toggle = useCallback((id: number) => {
    if (setRef.current.has(id)) {
      setRef.current.delete(id);
    } else {
      setRef.current.add(id);
    }
    forceUpdate();
  }, []);

  const selecionarTodas = useCallback((ids: number[]) => {
    ids.forEach((id) => setRef.current.add(id));
    forceUpdate();
  }, []);

  const desselecionarTodas = useCallback((ids: number[]) => {
    ids.forEach((id) => setRef.current.delete(id));
    forceUpdate();
  }, []);

  const limpar = useCallback(() => {
    setRef.current = new Set();
    forceUpdate();
  }, []);

  const resetar = useCallback((ids: number[]) => {
    setRef.current = new Set(ids);
    forceUpdate();
  }, []);

  const count = setRef.current.size;

  const totalPotencia = useCallback(
    (usinasPorId: Map<number, UsinaEolicaDenormalizada>) => {
      let total = 0;
      // forEach evita `for...of` em Set (precisaria de
      // --downlevelIteration no tsconfig do projeto, atual em ES5).
      setRef.current.forEach((id) => {
        const u = usinasPorId.get(id);
        if (u) total += u.potencia;
      });
      return total;
    },
    [],
  );

  const ids = useMemo(
    () => Array.from(setRef.current).sort((a, b) => a - b),
    // `nonce` muda a cada mutacao (toggle/selecionarTodas/limpar/resetar)
    // e e a unica dep confiavel — `count` falha quando `resetar` substitui
    // um set de N itens por outro de N itens distintos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nonce],
  );

  return {
    isSelecionada,
    toggle,
    selecionarTodas,
    desselecionarTodas,
    limpar,
    resetar,
    count,
    totalPotencia,
    ids,
  };
}
